import { useState } from "react";
import Head from "next/head";
import { connect } from "react-redux";
import getConfig from "next/config";

import PhoneBuyBackHeader from "@components/PhoneBuyBack/PhoneBuyBackHeader";
import PhoneBuyBackSteps from "@components/PhoneBuyBack/PhoneBuyBackSteps";
import FullWidthSection from "@components/FullWidthSection";
import { addAlert, addModal } from "@store/app/actions";
import { getBuyBackDevicesApi } from "@lib/api";
import ModalErrorBoundary from "@containers/ModalErrorBoundary";
import Modal from "@components/Modal";
import PhoneBuyBackSimulation from "@components/PhoneBuyBack/PhoneBuyBackSimulation";
import Button from "@components/Button";
import ErrorPage from "@containers/ErrorPage";
import { makeApiCall } from "@lib/api/helpers";
import PhoneBuyBackInfo from "@components/PhoneBuyBack/PhoneBuyBackInfo";
import PhoneBuyBackQA from "@components/PhoneBuyBack/PhoneBuyBackQA";
import Loading from "@components/Loading";

const {
  publicRuntimeConfig: {
    content: { websiteTitle, websiteUrlTitle },
    settings: { websiteName },
  },
} = getConfig();

const PhoneBuyBack = ({ addModal, addAlert }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [devices, setDeviceList] = useState();
  const [flowChart, setFlowChart] = useState();

  const startPhoneBuyBackSimulation = async () => {
    addModal("buy-back-simulation-modal");

    if (!isLoading && !(devices || flowChart)) {
      setIsLoading(true);
    }

    if (!(devices || flowChart)) {
      const response = await makeApiCall({
        apiCall: getBuyBackDevicesApi,
        formatResponse: (response) => {
          const groupedDevices = response.data.devices.reduce((acc, device) => {
            const { make, model, memory, offers } = device;

            if (acc[make]) {
              if (acc[make][model]) {
                if (acc[make][model][memory]) {
                  acc[make][model][memory] = offers[0];
                }

                acc[make][model][memory] = offers[0];
              } else {
                acc[make][model] = {};
                acc[make][model][memory] = offers[0];
              }
            } else {
              acc[make] = {};
              acc[make][model] = {};
              acc[make][model][memory] = offers[0];
            }
            return acc;
          }, {});

          return {
            devices: groupedDevices,
            questions: response.data.questions,
          };
        },
        preFail: () =>
          addAlert({
            message:
              "Ne pare rau. A aparut o eroare. Te rugam sa incerci mai tarziu.",
            variant: "error",
          }),
      });

      if (response) {
        setDeviceList(response.devices);
        setFlowChart(response.questions);
      }
    }

    if (isLoading) {
      setIsLoading(false);
    }
  };

  if (websiteName === "bricostore") {
    return <ErrorPage layout="404" />;
  }

  return (
    <>
      <Head>
        <title>
          Buy Back Telefoane | Cel mai mic pret din Romania | {websiteTitle}
        </title>

        <meta
          name="description"
          content={`Buy Back Telefoane ➤ Descopera valoarea telefonului tau pe ${websiteUrlTitle} ➤ Cel mai mic pret din Romania ⭐ ${websiteTitle}`}
        />

        <meta name="robots" content="index, follow" />

        <link
          rel="canonical"
          href={`https://${websiteUrlTitle}/buy-back-telefoane/`}
        />
      </Head>

      <FullWidthSection classProvided="bg-gradient-to-t from-[#CFE8F5] to-white px-[15px] py-[30px] overflow-hidden md:px-0 mb-[45px]">
        <PhoneBuyBackHeader
          onBuyBackSimlationClick={startPhoneBuyBackSimulation}
        />
      </FullWidthSection>

      <div className="space-y-[60px]">
        <PhoneBuyBackSteps />

        <PhoneBuyBackInfo />

        <PhoneBuyBackQA />
      </div>

      <div className="mx-auto mt-[30px] md:max-w-fit">
        <Button
          title="Vezi estimare de buy back"
          variant="secondary"
          size="small"
          display="block"
          onClick={startPhoneBuyBackSimulation}
        />
      </div>

      <ModalErrorBoundary>
        <Modal id="buy-back-simulation-modal" headerHasBorder={false}>
          {isLoading && <Loading height={100} />}

          {!isLoading && !(flowChart || devices) && (
            <div className="py-[50px] text-center">
              Ne pare rau. A aparut o eroare. Te rugam sa incerci mai tarziu.
            </div>
          )}

          {!isLoading && flowChart && devices && (
            <PhoneBuyBackSimulation devices={devices} flowChart={flowChart} />
          )}
        </Modal>
      </ModalErrorBoundary>
    </>
  );
};

PhoneBuyBack.pageLayout = "default";

export default connect(null, {
  addModal,
  addAlert,
})(PhoneBuyBack);
